import * as React from 'react';
import { MoodTrackingList, MoodV2 } from 'pages/Dashboard/types/moods.types';
import {
  getFormattedChartData,
  getMoodValueLabel,
} from 'pages/Dashboard/utils/trackingUtils';
import SingleLineChart from 'pages/Dashboard/components/SingleLineChart';
import PatientDetailsCard from 'components/PatientDetailsCard';
import { titleCase } from 'utils/utilMethods';
import { blue, orange } from 'theme';
import { Box } from '@mui/material';

const strokeColor = {
  [MoodV2.MOOD_ENERGY]: orange.Main,
  [MoodV2.MOOD_PLEASANTNESS]: blue[600],
};

interface MoodCardProps {
  cardData: {
    label: string;
    mood: MoodV2;
  };
  chartData: MoodTrackingList[];
  noData: boolean;
  isMoodLoading: boolean;
  timeFrame?: string;
  numberOfLogs?: number;
  onTimeFrameChange?: (value: string) => void;
  gridSize?: number;
}

export default function MoodCard({
  cardData,
  chartData,
  noData,
  isMoodLoading,
  timeFrame,
  gridSize = 6,
  numberOfLogs,
  onTimeFrameChange,
}: MoodCardProps): JSX.Element | null {
  const formattedChartData = getFormattedChartData(chartData);
  const average = React.useMemo(() => {
    if (chartData.length === 0) return 0;
    const total = chartData.reduce((sum, entry) => sum + entry.value, 0);
    return Math.round(total / chartData.length);
  }, [chartData]);

  const avgChipLabel = `Avg ${cardData.label}: ${titleCase(getMoodValueLabel(cardData.mood as MoodV2, average) ?? '')}`;

  return (
    <PatientDetailsCard
      key='mood-card'
      title={cardData.label}
      selectTimeFrame={timeFrame}
      onTimeFrameChange={onTimeFrameChange}
      avgChipLabel={avgChipLabel}
      noData={noData}
      isLoading={isMoodLoading}
      numberOfLogs={numberOfLogs ?? 0}
      gridProps={{
        item: true,
        xs: 12,
        md: gridSize,
      }}
    >
      <Box height='100%' width='100%' display='flex' alignItems='flex-end'>
        <SingleLineChart
          strokeColor={strokeColor[cardData.mood as MoodV2]}
          data={formattedChartData}
          dataKeyX='date'
          dataKeyLine='value'
          v2
          tickFormatter={(value) => (getMoodValueLabel(cardData.mood as MoodV2, value))}
        />
      </Box>
    </PatientDetailsCard>
  );
}
